/**
 * Invite friends: the signed-in person's referral code and join link, how many sign-ups it has
 * left, a plain explanation of invite-only access, and for company members a link to create beta
 * codes on FluxAccounts. All values come validated from `GET /api/referrals`.
 *
 * @module apps/web/src/pages/InvitePage
 */

import { useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, Gift, Loader2, Ticket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { referralsApi, type ReferralSummary } from '@/lib/referralsApi';

/** What the page is showing. */
type LoadState =
  | { /** Waiting for the API. */ kind: 'loading' }
  | {
      /** Summary loaded. */ kind: 'ready';
      /** Validated referral summary. */ summary: ReferralSummary;
    }
  | {
      /** The API refused or failed. */ kind: 'error';
      /** Server error code. */ code: string;
      /** Server message. */ message: string;
    };

/**
 * Loads the referral summary once and renders the code, link, remaining uses and the invite-only
 * explanation.
 */
export function InvitePage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    referralsApi.summary().then(response => {
      if (!active) return;
      if (response.success && response.data) setState({ kind: 'ready', summary: response.data });
      else
        setState({
          kind: 'error',
          code: response.error?.code || 'UNKNOWN_ERROR',
          message: response.error?.message || 'Could not load your referral code.',
        });
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Gift className="text-primary h-6 w-6" />
        <h1 className="text-2xl font-bold">Invite friends</h1>
      </div>

      <InviteOnlyExplainer summary={state.kind === 'ready' ? state.summary : null} />

      {state.kind === 'loading' && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your referral code…
        </div>
      )}

      {state.kind === 'error' && state.code === 'REFERRALS_UNKNOWN_SUBJECT' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No referral code yet</CardTitle>
            <CardDescription>
              FluxAccounts has not given this account a referral code. Codes are added to accounts
              over time; check back later, or ask a company member for a beta code to share.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {state.kind === 'error' && state.code !== 'REFERRALS_UNKNOWN_SUBJECT' && (
        <Card>
          <CardContent className="space-y-2 pt-6 text-sm">
            <p role="alert">{state.message}</p>
            {state.code === 'REFERRALS_UNAVAILABLE' && (
              <p className="text-muted-foreground">
                This is on our side. Try again in a little while.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {state.kind === 'ready' && <ReferralCard summary={state.summary} />}
      {state.kind === 'ready' &&
        state.summary.can_create_beta_codes &&
        state.summary.beta_codes_url && <BetaCodesCard url={state.summary.beta_codes_url} />}
    </div>
  );
}

/** Plain-language explanation of invite-only access, adjusted to the FluxAccounts sign-up mode. */
function InviteOnlyExplainer({
  summary,
}: {
  /** Loaded summary, or null while loading or after an error. */
  summary: ReferralSummary | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Fluxability is invite-only</CardTitle>
        <CardDescription>
          New people can join only with a code from someone who already uses Fluxability. Share your
          code or link. Your friend creates an account on FluxAccounts with it, then signs in here.
        </CardDescription>
      </CardHeader>
      {summary && summary.signup_mode !== 'invite_only' && (
        <CardContent className="text-muted-foreground text-sm">
          {summary.signup_mode === 'open'
            ? 'Sign-up is open right now, so friends can also join without a code. Your code still counts them as your referral.'
            : 'Sign-up is paused right now. Your code will work again when it reopens.'}
        </CardContent>
      )}
    </Card>
  );
}

/** The referral code and join link with copy buttons, and how many sign-ups remain. */
function ReferralCard({
  summary,
}: {
  /** Loaded referral summary. */
  summary: ReferralSummary;
}) {
  const exhausted = summary.uses_left === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span>Your referral code</span>
          <Badge variant={exhausted ? 'secondary' : 'default'}>
            {summary.uses_left} of {summary.max_uses} uses left
          </Badge>
        </CardTitle>
        <CardDescription>
          {exhausted
            ? 'Everyone you invited has joined. Thank you! Ask a company member if more people need a code.'
            : `Each sign-up with your code uses one of ${summary.max_uses}. ${summary.uses} used so far.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <CopyField label="Code" value={summary.code} monospace />
        <CopyField label="Join link" value={summary.join_url} />
      </CardContent>
    </Card>
  );
}

/** Read-only text with a copy button that confirms the copy in place. */
function CopyField({
  label,
  value,
  monospace = false,
}: {
  /** Visible label, also used in the button's accessible name. */
  label: string;
  /** Text to show and copy. */
  value: string;
  /** Use a monospace font, for codes. Defaults to false. */
  monospace?: boolean;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the text and copy it yourself.' });
    }
  };
  return (
    <div className="space-y-1">
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      <div className="flex gap-2">
        <Input
          readOnly
          value={value}
          aria-label={label}
          className={monospace ? 'font-mono tracking-wider' : undefined}
          onFocus={event => event.currentTarget.select()}
        />
        <Button
          type="button"
          variant="outline"
          onClick={copy}
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="ml-2 hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
    </div>
  );
}

/** Link for company members to create beta codes on FluxAccounts. */
function BetaCodesCard({
  url,
}: {
  /** Validated FluxAccounts `/codes` URL. */
  url: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Ticket className="h-4 w-4" /> Beta codes
        </CardTitle>
        <CardDescription>
          As a company member you can create beta codes with their own limits, for groups or events.
          Codes are created and revoked on FluxAccounts.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            Create beta codes <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}
